<!-- @format -->
<template>
    <BaseSlice
        :style="style"
        v-bind="navigationAttributes"
        class="gst-latest-deals">
        <div class="gst-latest-deals__wrapper">
            <div class="gst-latest-deals__header mb-10 text-center">
                <PrismicRichText :field="slice.primary.title" />
            </div>
            <LatestDealsCategoryFilter
                v-if="!loadingCategories && categories.length"
                v-model="selectedCategoryId"
                :items="categories"
                class="d-flex justify-center"
                @input="onChangeCategoryDo" />
            <DataLoading
                v-if="loading"
                class="mt-5 mb-5 pt-5 pb-5" />
            <div
                v-else
                class="gst-latest-deals__card_container py-6 d-flex flex-column">
                <EmptyStateCard
                    v-if="!events.length"
                    :title="title"
                    :message="message"
                    class="gst-latest-deals__empty-state-card row justify-center" />
                <div class="gst-latest-deals__card row wrap justify-center">
                    <div
                        v-for="(event, i) in events"
                        :key="i">
                        <LatestDealsCard
                            :event="event"
                            :data-test-id="$testId(`card.${i + 1}`)" />
                    </div>
                </div>
            </div>
            <SeeMoreButton
                v-if="!allItemsLoaded && !loading"
                :data-test-id="$testId('buttons.seeMore')"
                class="gst-latest-deals__see-more-button mx-auto d-flex"
                :loading="loadingSeeMore"
                @click="loadNextPage()" />
        </div>
        <TopOffersAnimation class="gst-slice-top-offers__background" />
    </BaseSlice>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';
import DataLoading from '@core/shared/components/loading/DataLoading';
import listConstants from '@core/utils/constants/list';
import SeeMoreButton from '@core/shared/components/buttons/SeeMoreButton.vue';
import NavMixin from '@tenant/app/slices/mixins/NavMixin.js';
import categoriesConstants from '@tenant/app/utils/constants/categories.js';
import EmptyStateCard from '@tenant/app/components/cards/EmptyStateCard.vue';
import LatestDealsCard from '../_components/LatestDealsCard.vue';
import LatestDealsCategoryFilter from '../_components/LatestDealsCategoryFilter.vue';
import TopOffersAnimation from '../_components/TopOffersAnimation.vue';

const STORE_EVENTS = 'topEvents';
const STORE_CATEGORIES = 'categories';

export default {
    name: 'LatestDeals',
    components: {
        LatestDealsCard,
        LatestDealsCategoryFilter,
        SeeMoreButton,
        DataLoading,
        EmptyStateCard,
        TopOffersAnimation,
    },
    mixins: [NavMixin],
    i18nOptions: {
        namespaces: 'main',
        keyPrefix: 'views.home.theHome._components.eventCardList',
    },
    testIdOptions: {
        keyPrefix: 'home.topEvents',
    },
    props: {
        slice: {
            type: Object,
            required: true,
        },
        context: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            lastAppliedFilter: {
                categoryId: null,
                city: {},
            },
            searchFilter: '',
        };
    },
    computed: {
        ...mapState({
            events: (state) => state[STORE_EVENTS].listView,
            categories: (state) => state[STORE_CATEGORIES].list,
            loadingEvents: (state) => state[STORE_EVENTS].loading,
            loadingCategories: (state) => state[STORE_CATEGORIES].loading,
            allItemsLoaded: (state) => state[STORE_EVENTS].allItemsLoaded,
            topEventsFilter: (state) => state[STORE_EVENTS].filter,
        }),
        style() {
            return {
                background: `url(${this.slice.primary.bg_image.url})`,
                backgroundSize: 'cover',
            };
        },
        selectedCity() {
            return this.topEventsFilter.city || {};
        },
        selectedCategoryId: {
            get: function () {
                return this.topEventsFilter.categoryId;
            },
            set: function (value) {
                const userAnalytics = !!(value || this.selectedCity.name);
                const filter = { city: this.selectedCity, ...this.selectedCategory(value) };

                if (value === categoriesConstants.LIST_ID.ALL) {
                    filter.categoryId = null;
                    filter.search = null;
                }

                this.updateFilter(filter);
                this.getEventsDebounced({
                    refresh: true,
                    filter: filter,
                    userAnalytics: userAnalytics,
                });
            },
        },
        loading() {
            return this.loadingEvents && !this.events.length;
        },
        loadingSeeMore() {
            return this.loadingEvents;
        },
        title() {
            return this.$t('_common:messages.noPopularEventsFound.title');
        },
        message() {
            return this.$t('_common:messages.noPopularEventsFound.message');
        },
    },
    methods: {
        ...mapActions({
            getEvents: `${STORE_EVENTS}/get`,
            updateFilter: `${STORE_EVENTS}/updateFilter`,
            getCategories: `${STORE_CATEGORIES}/get`,
        }),
        loadNextPage() {
            this.getEvents({
                loadNextPage: true,
                filter: { city: this.selectedCity, ...this.selectedCategory() },
            });
        },
        selectedCategory(id) {
            const category = this.categories.find((item) => item.id === id);

            if (!category) {
                return null;
            }
            return {
                categoryId: category.id,
                search: category.search,
            };
        },
        onChangeCategoryDo(value) {
            const updatedFilters = {};
            if (!value) {
                updatedFilters.categoryId = null;
                updatedFilters.search = null;
            }
            this.updateFilter(updatedFilters);
        },
        getEventsDebounced: debounce(function (params = { refresh: false }) {
            const initialFilter = { ...this.topEventsFilter };
            delete initialFilter.search;

            if (isEqual(this.lastAppliedFilter, initialFilter) && params.refresh) {
                return;
            }

            this.lastAppliedFilter = { ...initialFilter };

            this.getEvents(params);
        }, listConstants.DEBOUNCE_DELAY),
    },
    created() {
        this.getCategories();
        this.searchFilter = {
            city: {
                name: this.context.locationToPrepopulate?.city,
                stateCode: this.context.locationToPrepopulate?.stateCode,
                countryCode: this.context.locationToPrepopulate?.countryCode,
            },
        };
        this.getEvents({ filter: this.searchFilter });
    },
};
</script>

<style lang="scss">
@import '@scssVariables';
@import '@scssMixins';

.gst-slice-top-offers__background {
    margin-top: 200px;
}

.gst-latest-deals__empty-state-card {
    text-align: center;

    .gst-empty-state-card__content {
        width: 657px;
        padding: 40px 72px;
        margin-top: 96px;
        background-color: rgba(theme-color-hex('quaternary'), 0.8);
        min-height: 140px;
    }

    .gst-empty-state-card-title {
        margin-bottom: theme-spacing(3);
    }
}

@include mobile-only {
    .gst-latest-deals__empty-state-card {
        padding: theme-spacing(0, 6);

        .gst-empty-state-card__content {
            width: auto;
            margin-top: 64px;
            min-height: 164px;
        }
    }
}
</style>
